import { directedStyleCastFit, type DirectedStyleCastFit } from 'fym-shared/src/music24/directedCastRules';
import {
  sameDirectedStyleLook,
  type DirectedCastEntry,
  type DirectedStory,
  type DirectedStyle,
} from 'fym-shared/src/music24/directedVideoApi';
import type { MusicVideoSongMapSection } from 'fym-shared/src/music24/musicVideoApi';
import { sectionDisplayName, sectionToneOf, type SectionTone } from '../../shell/sectionBar';

/** True when two stories have the same words (the edit changed nothing). Pure. */
export function sameStory(a: DirectedStory, b: DirectedStory): boolean {
  return (
    a.id === b.id &&
    a.title === b.title &&
    a.logline === b.logline &&
    a.beats.length === b.beats.length &&
    a.beats.every(
      (beat, index) => beat.sectionIndex === b.beats[index]?.sectionIndex && beat.text === b.beats[index]?.text
    )
  );
}

/** True when two styles have the same fields. Pure. */
export function sameStyle(a: DirectedStyle, b: DirectedStyle): boolean {
  return (
    a.id === b.id &&
    a.title === b.title &&
    a.camera === b.camera &&
    a.colourAndLight === b.colourAndLight &&
    a.texture === b.texture &&
    a.pacing === b.pacing &&
    a.effectFamilies.length === b.effectFamilies.length &&
    a.effectFamilies.every((family) => b.effectFamilies.includes(family))
  );
}

/**
 * The choice that is selected first: the stored pick when it is one of the choices, else the first
 * choice. Pure.
 */
export function initialChoiceId<T extends { id: string }>(choices: readonly T[], picked: T | null): string | null {
  if (picked !== null && choices.some((choice) => choice.id === picked.id)) {
    return picked.id;
  }
  return picked?.id ?? choices[0]?.id ?? null;
}

/** The edits of the stored pick: its words when they differ from the choice of the same id. Pure. */
export function initialDrafts<T extends { id: string }>(
  choices: readonly T[],
  picked: T | null,
  same: (a: T, b: T) => boolean
): Record<string, T> {
  if (picked === null) {
    return {};
  }
  const original = choices.find((choice) => choice.id === picked.id);
  return original === undefined || !same(original, picked) ? { [picked.id]: picked } : {};
}

/** A key that changes when the director writes new choices: the step starts again with them. Pure. */
export function choicesKey(choices: readonly { id: string; title: string }[] | null): string {
  return (choices ?? []).map((choice) => `${choice.id}:${choice.title}`).join('|');
}

export interface BeatSection {
  name: string;
  tone: SectionTone;
}

/** The name and the colour of the section of a beat (by `sectionIndex`), from the song map. Pure. */
export function beatSectionOf(sections: readonly MusicVideoSongMapSection[], sectionIndex: number): BeatSection {
  const section = sections.find((item) => item.index === sectionIndex);
  if (section === undefined) {
    return { name: String(sectionIndex + 1), tone: 'verse' };
  }
  return { name: sectionDisplayName(section.label), tone: sectionToneOf(section.kind) };
}

/** A new id for a style that the artist writes ("Write my own"). */
export function newOwnStyleId(): string {
  return `own-${Date.now().toString(36)}${Math.random().toString(36).slice(2, 6)}`;
}

/**
 * A style needs a name and one more line (design d2). The effect families can be any subset, also
 * none (owner decision: the artist switches them on and off before the script). Pure.
 */
export function isOwnStyleValid(style: DirectedStyle): boolean {
  const lines = [style.camera, style.colourAndLight, style.texture, style.pacing].filter((line) => line.trim() !== '');
  return style.title.trim() !== '' && lines.length > 0;
}

/** A style with trimmed fields, as the PUT sends it. Pure. */
export function trimmedStyle(style: DirectedStyle): DirectedStyle {
  return {
    ...style,
    title: style.title.trim(),
    camera: style.camera.trim(),
    colourAndLight: style.colourAndLight.trim(),
    texture: style.texture.trim(),
    pacing: style.pacing.trim(),
  };
}

/**
 * How the picked style fits the art style of the cast (fix 2026-10-18): the director's judgement for
 * its own style with the same look texts (as the API keeps it, `withTrustedCastFit`), else the rule `directedStyleCastFit` (an edited style or
 * the artist's own). Null with no picked style. Pure.
 */
export function pickedStyleCastFit(
  picked: DirectedStyle | null,
  original: DirectedStyle | undefined,
  cast: readonly DirectedCastEntry[]
): DirectedStyleCastFit | null {
  if (picked === null) {
    return null;
  }
  const trusted = original !== undefined && sameDirectedStyleLook(trimmedStyle(picked), trimmedStyle(original));
  const style: DirectedStyle = trusted ? picked : { ...picked, castFit: undefined, castFitReason: undefined };
  return directedStyleCastFit(style, cast);
}
